<template>
  <div class="ap" :class="['ap-' + kind, { compact }]" aria-hidden="true">
    <div v-if="kind === 'YesNo' || kind === 'Triple'" class="ap-seg">
      <span class="yes">بله</span><span class="no">خیر</span><span v-if="kind === 'Triple'" class="none">ندارد</span>
    </div>
    <div v-else-if="kind === 'Score'" class="ap-score"><span v-for="n in 5" :key="n" :class="{ on: n === 4 }">{{ fa(n) }}</span></div>
    <div v-else-if="kind === 'Number'" class="ap-number">
      <span class="step">+</span><span class="field">{{ rules.min_value != null ? fa(rules.min_value) : '۰' }}</span><span class="step">−</span>
      <small v-if="unit">{{ unit }}</small>
      <small v-if="range" class="range">{{ range }}</small>
    </div>
    <div v-else-if="kind === 'Price'" class="ap-line"><span class="field ltr">۰</span><small>ریال</small><small v-if="range" class="range">{{ range }}</small></div>
    <div v-else-if="kind === 'Input'" class="ap-line"><span class="field">پاسخ کوتاه</span><small v-if="rules.regex" class="range vn-mono">{{ rules.regex.slice(0, 24) }}</small></div>
    <div v-else-if="kind === 'Description'" class="ap-text"><span class="field area">توضیحات…</span><small v-if="rules.max_lenght" class="range">تا {{ fa(rules.max_lenght) }} نویسه</small></div>
    <div v-else-if="choices" class="ap-choices" :class="{ drop: kind === 'DropDownList' }">
      <template v-if="kind === 'DropDownList'"><span class="field">{{ firstChoice || 'یک گزینه را انتخاب کنید' }}<b>⌄</b></span><small class="range">{{ fa(choiceList.length) }} گزینه</small></template>
      <template v-else>
        <span v-for="(choice, index) in shownChoices" :key="choice._k || index" class="choice"><i :class="kind === 'Multichoice' ? 'mk-box' : 'mk-dot'" />{{ choice.answer || '—' }}</span>
        <small v-if="choiceList.length > shownChoices.length" class="range">و {{ fa(choiceList.length - shownChoices.length) }} گزینه‌ی دیگر</small>
        <small v-if="countRule" class="range">{{ countRule }}</small>
      </template>
    </div>
  </div>
</template>

<script>
import { KINDS, fa } from '../designerModel';

// A small, faithful sketch of how the field app renders one answer input.
export default {
  name: 'AnswerPreview',
  props: {
    kind: { type: String, required: true },
    question: { type: Object, required: true },
    rules: { type: Object, default: () => ({}) },
    compact: { type: Boolean, default: false },
  },
  computed: {
    choices() { return KINDS[this.kind] && KINDS[this.kind].choices; },
    choiceList() { return (this.choices && this.question.choices[this.choices]) || []; },
    shownChoices() { return this.choiceList.slice(0, this.compact ? 3 : 6); },
    firstChoice() { return this.choiceList[0] && this.choiceList[0].answer; },
    unit() { return this.question.short_text; },
    range() {
      const { min_value: min, max_value: max } = this.rules;
      if (min != null && max != null) return `${fa(min)} تا ${fa(max)}`;
      if (max != null) return `حداکثر ${fa(max)}`;
      return min != null ? `حداقل ${fa(min)}` : '';
    },
    countRule() {
      const { min_choice_count: min, max_choice_count: max } = this.rules;
      if (min && max) return `${fa(min)} تا ${fa(max)} انتخاب`;
      if (max) return `حداکثر ${fa(max)} انتخاب`;
      return min ? `حداقل ${fa(min)} انتخاب` : '';
    },
  },
  methods: { fa },
};
</script>

<style scoped>
.ap { font-size: 11.5px; color: var(--text-2); }
.ap small { color: var(--text-3); font-size: 10.5px; }
.ap .range { margin-inline-start: 6px; padding: 1px 6px; border-radius: var(--r-xs); background: var(--navy-50); color: var(--text-2); font-family: var(--font-num); }
.field { display: inline-flex; align-items: center; justify-content: space-between; gap: 8px; min-width: 120px; height: 28px; padding: 0 10px;
  border: 1px solid var(--line-strong); border-radius: var(--r-xs); background: var(--surface); color: var(--text-4); font-family: var(--font-num); }
.field b { color: var(--text-3); font-weight: 400; }
.field.area { height: 40px; align-items: flex-start; padding-top: 5px; min-width: 180px; }
.ltr { direction: ltr; justify-content: flex-start; }
.ap-seg { display: inline-flex; gap: 4px; }
.ap-seg span { min-width: 44px; padding: 4px 8px; text-align: center; border: 1px solid var(--line-strong); border-radius: var(--r-xs); background: var(--surface); font-weight: 800; }
.ap-seg .yes { border-color: var(--navy-900); background: var(--teal-500); color: var(--navy-900); box-shadow: 2px 2px 0 var(--navy-900); }
.ap-score { display: inline-flex; gap: 4px; }
.ap-score span { width: 26px; height: 26px; display: grid; place-items: center; border: 1px solid var(--line-strong); border-radius: var(--r-xs); background: var(--surface); font: 800 11px/1 var(--font-num); }
.ap-score span.on { background: var(--amber-500); border-color: var(--navy-900); color: var(--navy-900); }
.ap-number, .ap-line, .ap-text { display: flex; align-items: center; flex-wrap: wrap; gap: 4px; }
.ap-number .step { width: 28px; height: 28px; display: grid; place-items: center; border: 1px solid var(--line-strong); border-radius: var(--r-xs); background: var(--navy-50); color: var(--navy-900); font-weight: 900; }
.ap-number .field { min-width: 52px; justify-content: center; color: var(--text); font-weight: 800; }
.ap-choices { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 8px; }
.choice { display: inline-flex; align-items: center; gap: 5px; padding: 3px 9px 3px 7px; border: 1px solid var(--line); border-radius: var(--r-xs); background: var(--surface); max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.choice i { flex: none; width: 10px; height: 10px; border: 1.5px solid var(--text-3); }
.choice i.mk-dot { border-radius: 50%; }
.choice i.mk-box { border-radius: 2px; }
.compact .field { min-width: 96px; height: 24px; }
.compact .field.area { height: 30px; }
</style>
